import Phaser from '../lib/phaser.js';
import { SCENE_KEYS } from './scene-keys.js';
import { BaseScene } from './base-scene.js';
import { MONSTER_ASSET_KEYS, BATTLE_BACKGROUND_ASSET_KEYS } from '../assets/asset-keys.js';
import { KENNEY_FUTURE_NARROW_FONT_NAME } from '../assets/font-keys.js';
import { netManager } from '../net/index.js';
import { dataManager } from '../utils/data-manager.js';

// ── Server-authoritative PvP battle scene — styled like the wild battle. ──
//
// A pure VIEW layer over the realtime server's PvP engine: it renders YOUR lead monster vs the FOE's
// in the familiar Pokémon-style layout (monsters on platforms, HP panels, a message box, and a
// FIGHT / ITEM / OFFER / FLEE menu) and only ever SUBMITS INTENT. The server resolves every turn,
// computes all damage, and owns the wager — the client never computes damage or money.
//
// Actions: FIGHT (move), ITEM (heal — capture is NOT possible in PvP), FLEE (= forfeit, lose stake),
// OFFER → Draw (both keep stake) or Forfeit-for-half (recover half your stake). When the OPPONENT
// proposes, an Accept / Decline prompt appears.

const CATCHIMON_BASE_SCALE = 3.0; // 80px Catchimon ≈ the 256px legacy sprites
const ELEMENT_SPRITE = Object.freeze({
  flame: { key: MONSTER_ASSET_KEYS.CATCHIMON_FIRE_01, scale: CATCHIMON_BASE_SCALE },
  tide: { key: MONSTER_ASSET_KEYS.CATCHIMON_WATER_11, scale: CATCHIMON_BASE_SCALE },
  verdant: { key: MONSTER_ASSET_KEYS.CATCHIMON_GRASS_19, scale: CATCHIMON_BASE_SCALE },
  frost: { key: MONSTER_ASSET_KEYS.RIMEHOWL, scale: 1 },
  stone: { key: MONSTER_ASSET_KEYS.RIMEHOWL, scale: 1 },
  gale: { key: MONSTER_ASSET_KEYS.VOLTCRAG, scale: 1 },
  venom: { key: MONSTER_ASSET_KEYS.GLOOMFANG, scale: 1 },
  beast: { key: MONSTER_ASSET_KEYS.GLOOMFANG, scale: 1 },
});
const DEFAULT_SPRITE = { key: MONSTER_ASSET_KEYS.GLOOMFANG, scale: 1 };

const C = {
  panel: 0x0d1f15,
  panelStroke: 0x34d399,
  gold: '#f5b23e',
  text: '#eafff2',
  sub: '#9fc4ad',
  danger: '#ff9a9a',
};

/**
 * @typedef PvpBattleSceneData
 * @type {object}
 * @property {string} battleId
 * @property {'arena' | 'badlands' | 'duel'} kind
 * @property {string | null} opponentSessionId
 * @property {import('@mn/shared').BattleMonster} you   YOUR lead monster (drives the move menu)
 * @property {import('@mn/shared').BattleMonster} foe   the opponent's lead monster
 * @property {{ itemId: string, name: string, qty: number }[]} [bag] heal items available in-battle
 */

export class PvpBattleScene extends BaseScene {
  /** @type {PvpBattleSceneData} */ #data;
  /** @type {(() => void) | undefined} */ #netUnsub;
  /** @type {boolean} awaiting the server's turn resolution (menu locked) */ #awaitingTurn;
  /** @type {boolean} the battle has ended */ #ended;
  /** @type {{ itemId: string, name: string, qty: number }[]} */ #bag;
  /** @type {{ type: 'draw'|'forfeitHalf' } | null} a proposal FROM the opponent awaiting our response */ #incomingOffer;
  /** @type {boolean} we have a proposal out, awaiting their response */ #offerPending;

  // UI handles
  /** @type {Phaser.GameObjects.Graphics} */ #youBar;
  /** @type {Phaser.GameObjects.Graphics} */ #foeBar;
  /** @type {Phaser.GameObjects.Text} */ #youHpText;
  /** @type {Phaser.GameObjects.Text} */ #logText;
  /** @type {Phaser.GameObjects.Container} */ #menu;
  /** @type {Phaser.GameObjects.Image} */ #youSprite;
  /** @type {Phaser.GameObjects.Image} */ #foeSprite;
  /** @type {{ hp: number, max: number }} */ #youHp;
  /** @type {{ hp: number, max: number }} */ #foeHp;

  constructor() {
    super({ key: SCENE_KEYS.PVP_BATTLE_SCENE });
  }

  /** @param {PvpBattleSceneData} data */
  init(data) {
    super.init(data);
    this.#data = data;
    this.#awaitingTurn = false;
    this.#ended = false;
    this.#bag = Array.isArray(data.bag) ? data.bag.slice() : [];
    this.#incomingOffer = null;
    this.#offerPending = false;
    this.#youHp = { hp: data.you.maxHp, max: data.you.maxHp };
    this.#foeHp = { hp: data.foe.maxHp, max: data.foe.maxHp };
  }

  create() {
    super.create();
    const { width, height } = this.scale;

    // ── full-screen background (scaled to COVER so the world never shows through) ──
    const bg = this.add.image(width / 2, height / 2, BATTLE_BACKGROUND_ASSET_KEYS.FOREST, 0).setDepth(0);
    const cover = Math.max(width / bg.width, height / bg.height);
    bg.setScale(cover);

    // ── foe (top-right) on a platform ──
    this.#platform(width * 0.7, height * 0.34, width * 0.26);
    const foeArt = ELEMENT_SPRITE[this.#data.foe.element] ?? DEFAULT_SPRITE;
    this.#foeSprite = this.add
      .image(width * 0.7, height * 0.3, foeArt.key)
      .setOrigin(0.5, 1)
      .setScale(0.5 * foeArt.scale)
      .setDepth(2);
    this.#foeInfoPanel(width * 0.06, height * 0.08, this.#data.foe, 'foe');

    // ── you (bottom-left) on a platform ──
    this.#platform(width * 0.28, height * 0.66, width * 0.3);
    const youArt = ELEMENT_SPRITE[this.#data.you.element] ?? DEFAULT_SPRITE;
    this.#youSprite = this.add
      .image(width * 0.28, height * 0.66, youArt.key)
      .setOrigin(0.5, 1)
      .setScale(0.62 * youArt.scale)
      .setFlipX(true)
      .setDepth(2);
    this.#youInfoPanel(width * 0.6, height * 0.5, this.#data.you, 'you');

    // ── message box (bottom-left) ──
    const boxW = width * 0.56;
    const boxH = height * 0.2;
    this.add
      .rectangle(width * 0.02, height * 0.78, boxW, boxH, C.panel, 0.95)
      .setOrigin(0)
      .setStrokeStyle(3, C.panelStroke, 0.9)
      .setDepth(3);
    this.#logText = this.add
      .text(width * 0.04, height * 0.8, this.#kindBanner(), {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '20px',
        color: C.text,
        wordWrap: { width: boxW - width * 0.04 },
        lineSpacing: 6,
      })
      .setDepth(4);

    this.#buildMainMenu();
    this.#redrawBars();
    this.#wireNet();

    this.cameras.main.fadeIn(250, 0, 0, 0);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.#netUnsub?.());
  }

  // ── presentation helpers ────────────────────────────────────────────────────

  /** A soft stone platform ellipse under a monster. */
  #platform(x, y, w) {
    this.add.ellipse(x, y, w, w * 0.32, 0x2d4a36, 0.55).setDepth(1);
    this.add.ellipse(x, y, w * 0.82, w * 0.24, 0x3c6049, 0.5).setDepth(1);
  }

  /** @param {import('@mn/shared').BattleMonster} m */
  #youInfoPanel(x, y, m, who) {
    this.#infoPanel(x, y, m, who);
  }
  #foeInfoPanel(x, y, m, who) {
    this.#infoPanel(x, y, m, who);
  }
  #infoPanel(x, y, m, who) {
    const w = 300;
    const h = 64;
    this.add.rectangle(x, y, w, h, C.panel, 0.95).setOrigin(0).setStrokeStyle(3, C.panelStroke, 0.85).setDepth(3);
    this.add
      .text(x + 14, y + 8, `${m.name}`, { fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME, fontSize: '18px', color: C.text })
      .setDepth(4);
    this.add
      .text(x + w - 14, y + 8, `Lv.${m.level}`, { fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME, fontSize: '16px', color: C.gold })
      .setOrigin(1, 0)
      .setDepth(4);
    const bar = this.add.graphics().setDepth(4);
    if (who === 'you') {
      this.#youBar = bar;
      this.#youBar.setData('pos', { x: x + 14, y: y + 38, w: w - 28 });
      this.#youHpText = this.add
        .text(x + w - 14, y + 34, '', { fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME, fontSize: '13px', color: C.sub })
        .setOrigin(1, 0)
        .setDepth(5);
    } else {
      this.#foeBar = bar;
      this.#foeBar.setData('pos', { x: x + 14, y: y + 38, w: w - 28 });
    }
  }

  #kindBanner() {
    if (this.#data.kind === 'duel') return 'Duel started! What will you do?';
    return 'Arena battle! What will you do?';
  }

  #redrawBars() {
    if (this.#youBar) {
      const p = this.#youBar.getData('pos');
      this.#drawBar(this.#youBar, p.x, p.y, p.w, this.#youHp.hp / this.#youHp.max);
      this.#youHpText?.setText(`${Math.max(0, Math.round(this.#youHp.hp))}/${this.#youHp.max}`);
    }
    if (this.#foeBar) {
      const p = this.#foeBar.getData('pos');
      this.#drawBar(this.#foeBar, p.x, p.y, p.w, this.#foeHp.hp / this.#foeHp.max);
    }
  }

  #drawBar(gfx, x, y, w, frac) {
    const h = 14;
    const f = Phaser.Math.Clamp(frac, 0, 1);
    const color = f > 0.5 ? 0x6ce06a : f > 0.2 ? 0xe6c84a : 0xe6604a;
    gfx.clear();
    gfx.fillStyle(0x000000, 0.5).fillRoundedRect(x, y, w, h, 5);
    if (f > 0) gfx.fillStyle(color, 1).fillRoundedRect(x, y, Math.max(4, w * f), h, 5);
    gfx.lineStyle(2, 0x0a0c12, 1).strokeRoundedRect(x, y, w, h, 5);
  }

  // ── menus (client-side UI state; all actions just submit intent) ─────────────

  /** The 2×2 root menu: FIGHT / ITEM / OFFER / FLEE — mirrors the wild battle. */
  #buildMainMenu() {
    this.#rebuildMenu([
      { label: 'FIGHT', onClick: () => this.#buildMoveMenu() },
      { label: 'ITEM', onClick: () => this.#buildItemMenu() },
      { label: 'OFFER', onClick: () => this.#buildOfferMenu() },
      { label: 'FLEE', color: C.danger, onClick: () => this.#confirmFlee() },
    ]);
  }

  #buildMoveMenu() {
    const moves = this.#data.you.moves.slice(0, 4);
    const opts = moves.map((mv) => ({ label: mv.name, onClick: () => this.#chooseMove(mv.id) }));
    opts.push({ label: '‹ Back', color: C.sub, onClick: () => this.#buildMainMenu() });
    this.#rebuildMenu(opts);
  }

  #buildItemMenu() {
    const opts = this.#bag
      .filter((b) => (b.qty ?? 0) > 0)
      .map((b) => ({ label: `${b.name} ×${b.qty}`, onClick: () => this.#useItem(b.itemId) }));
    if (opts.length === 0) opts.push({ label: 'No items', color: C.sub, onClick: () => {} });
    opts.push({ label: '‹ Back', color: C.sub, onClick: () => this.#buildMainMenu() });
    this.#rebuildMenu(opts);
  }

  #buildOfferMenu() {
    const opts = [
      { label: 'Offer Draw', onClick: () => this.#offer('offerDraw') },
      { label: 'Forfeit ½', onClick: () => this.#offer('offerForfeitHalf') },
      { label: '‹ Back', color: C.sub, onClick: () => this.#buildMainMenu() },
    ];
    this.#rebuildMenu(opts);
  }

  /** Accept/Decline prompt shown when the OPPONENT proposes a draw / forfeit-for-half. */
  #buildOfferPrompt() {
    const off = this.#incomingOffer;
    const what = off?.type === 'draw' ? 'a DRAW' : 'to abandon for HALF the pot';
    this.#appendLog(`Opponent offered ${what}.`);
    this.#rebuildMenu([
      { label: 'Accept', onClick: () => this.#respondOffer(true) },
      { label: 'Decline', color: C.danger, onClick: () => this.#respondOffer(false) },
    ]);
  }

  /** (Re)draw the bottom-right action panel from a list of {label,color,onClick}. */
  #rebuildMenu(options) {
    const { width, height } = this.scale;
    this.#menu?.destroy();
    const panelW = width * 0.42;
    const panelH = height * 0.2;
    const px = width - panelW - width * 0.02;
    const py = height * 0.78;
    this.#menu = this.add.container(0, 0).setDepth(5);
    const bg = this.add
      .rectangle(px, py, panelW, panelH, C.panel, 0.96)
      .setOrigin(0)
      .setStrokeStyle(3, C.panelStroke, 0.9);
    this.#menu.add(bg);

    const cols = 2;
    const cellW = panelW / cols - 18;
    const rowH = 44;
    options.forEach((opt, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const cx = px + 18 + col * (panelW / cols);
      const cy = py + 16 + row * rowH;
      const btn = this.add
        .text(cx, cy, opt.label, {
          fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
          fontSize: '19px',
          color: opt.color ?? C.text,
          fixedWidth: cellW,
        })
        .setInteractive({ useHandCursor: true });
      btn.on('pointerover', () => btn.setColor(C.gold));
      btn.on('pointerout', () => btn.setColor(opt.color ?? C.text));
      btn.on('pointerdown', () => {
        if (this.#ended) return;
        opt.onClick();
      });
      this.#menu.add(btn);
    });
  }

  #setMenuEnabled(enabled) {
    this.#menu?.setAlpha(enabled ? 1 : 0.4);
    this.#menu?.iterate((child) => {
      if (!child.setInteractive) return;
      if (enabled) child.setInteractive({ useHandCursor: true });
      else child.disableInteractive?.();
    });
  }

  // ── action intents (sent to the server) ──────────────────────────────────────

  #lockForTurn(note) {
    this.#awaitingTurn = true;
    this.#setMenuEnabled(false);
    this.#appendLog(note);
  }

  #chooseMove(moveId) {
    if (this.#ended || this.#awaitingTurn) return;
    const client = netManager.client;
    if (!client) return;
    this.#lockForTurn('You chose your move. Waiting for the opponent…');
    client.battleAction(this.#data.battleId, moveId);
  }

  #useItem(itemId) {
    if (this.#ended || this.#awaitingTurn) return;
    const client = netManager.client;
    if (!client) return;
    // consume locally (persisted) so the bag can't be reused for free across battles
    this.#consumeItem(itemId);
    this.#lockForTurn('You used an item. Waiting for the opponent…');
    client.battleUseItem(this.#data.battleId, itemId);
  }

  /** Decrement one of `itemId` from the persistent inventory + the in-battle bag copy. */
  #consumeItem(itemId) {
    const slot = this.#bag.find((b) => b.itemId === itemId);
    if (slot) slot.qty = Math.max(0, (slot.qty ?? 0) - 1);
    try {
      const inv = dataManager.getInventory(this) || [];
      const next = inv
        .map((s) => (s.item?.id === itemId ? { item: s.item, quantity: Math.max(0, (s.quantity ?? 0) - 1) } : s))
        .filter((s) => (s.quantity ?? 0) > 0);
      dataManager.updateInventory(next);
      dataManager.saveData?.();
    } catch {
      /* best-effort — the server applies the heal regardless */
    }
  }

  #offer(action) {
    if (this.#ended || this.#offerPending) return;
    const client = netManager.client;
    if (!client) return;
    this.#offerPending = true;
    if (action === 'offerDraw') {
      client.battleOfferDraw(this.#data.battleId);
      this.#appendLog('You offered a draw. Waiting for a response…');
    } else {
      client.battleOfferForfeitHalf(this.#data.battleId);
      this.#appendLog('You offered to abandon for half the pot. Waiting…');
    }
    this.#buildMainMenu();
  }

  #respondOffer(accept) {
    const client = netManager.client;
    if (!client) return;
    this.#incomingOffer = null;
    if (accept) client.battleAcceptOffer(this.#data.battleId);
    else {
      client.battleDeclineOffer(this.#data.battleId);
      this.#buildMainMenu();
    }
  }

  #confirmFlee() {
    if (this.#ended) return;
    const stakeNote = this.#data.kind === 'duel' ? ' You will lose your wager.' : '';
    this.#appendLog(`Fleeing forfeits the battle.${stakeNote}`);
    this.#rebuildMenu([
      { label: 'Confirm', color: C.danger, onClick: () => netManager.client?.battleForfeit(this.#data.battleId) },
      { label: '‹ Back', color: C.sub, onClick: () => this.#buildMainMenu() },
    ]);
  }

  // ── network ──────────────────────────────────────────────────────────────────

  #wireNet() {
    const client = netManager.client;
    if (!client) {
      this.#appendLog('Connection lost. Returning to the world…');
      this.time.delayedCall(1500, () => this.#returnToWorld());
      return;
    }
    this.#netUnsub = client.on((event) => {
      if (event.type !== 'server') return;
      const msg = event.message;
      if (msg.t === 'battleState' && msg.battleId === this.#data.battleId) this.#onBattleState(msg);
      else if (msg.t === 'battleEnd' && msg.battleId === this.#data.battleId) this.#onBattleEnd(msg);
      else if (msg.t === 'duelResult') this.#onDuelResult(msg);
    });
  }

  /** @param {Extract<import('@mn/shared').ServerMessage, { t: 'battleState' }>} msg */
  #onBattleState(msg) {
    this.#youHp = { hp: msg.you.hp, max: msg.you.maxHp };
    this.#foeHp = { hp: msg.foe.hp, max: msg.foe.maxHp };
    this.#redrawBars();
    for (const line of msg.log) this.#appendLog(line);
    if (this.#foeSprite) this.#flash(this.#foeSprite);
    if (this.#youSprite) this.#flash(this.#youSprite);

    // a proposal from the opponent → show Accept/Decline; our own pending offer just keeps waiting
    const offer = msg.pendingOffer ?? null;
    if (offer && offer.from === 'foe') {
      this.#incomingOffer = { type: offer.type };
      this.#awaitingTurn = false;
      this.#setMenuEnabled(true);
      this.#buildOfferPrompt();
      return;
    }
    if (offer && offer.from === 'you') {
      this.#offerPending = true; // still awaiting their response
      return;
    }
    // no/!cleared offer → a normal turn resolved; re-enable input
    this.#offerPending = false;
    this.#awaitingTurn = false;
    if (!this.#ended) {
      this.#setMenuEnabled(true);
      this.#buildMainMenu();
    }
  }

  /** @param {Extract<import('@mn/shared').ServerMessage, { t: 'battleEnd' }>} msg */
  #onBattleEnd(msg) {
    this.#ended = true;
    this.#setMenuEnabled(false);
    const rt = msg.resultType;
    let banner;
    let color;
    if (rt === 'draw') {
      banner = 'DRAW';
      color = '#cfe0ff';
    } else if (msg.youWon) {
      banner = 'VICTORY';
      color = '#6ce06a';
    } else {
      banner = rt === 'forfeitHalf' ? 'ABANDONED' : 'DEFEAT';
      color = C.danger;
    }
    const goldNote = msg.youWon && msg.rewardGold ? ` +${msg.rewardGold} gold` : '';
    this.#appendLog(`${banner}.${goldNote}`);
    this.#showResultBanner(banner, color);
    this.time.delayedCall(2600, () => this.#returnToWorld());
  }

  /** @param {Extract<import('@mn/shared').ServerMessage, { t: 'duelResult' }>} msg */
  #onDuelResult(msg) {
    // reconcile the Gold HUD to the authoritative balance on next world load; show a quick note here
    void msg;
  }

  #flash(sprite) {
    this.tweens.add({ targets: sprite, alpha: 0.4, duration: 90, yoyo: true });
  }

  #appendLog(line) {
    if (!this.#logText) return;
    const lines = `${this.#logText.text}\n${line}`.split('\n');
    this.#logText.setText(lines.slice(-4).join('\n'));
  }

  #showResultBanner(text, color) {
    const { width, height } = this.scale;
    const banner = this.add
      .text(width / 2, height / 2, text, {
        fontFamily: KENNEY_FUTURE_NARROW_FONT_NAME,
        fontSize: '64px',
        color,
        backgroundColor: '#000000aa',
        padding: { x: 28, y: 16 },
      })
      .setOrigin(0.5)
      .setDepth(10)
      .setAlpha(0);
    this.tweens.add({ targets: banner, alpha: 1, duration: 300 });
  }

  #returnToWorld() {
    if (this.scene.get(SCENE_KEYS.WORLD_SCENE)) {
      this.scene.stop();
      this.scene.resume(SCENE_KEYS.WORLD_SCENE);
      // Do NOT bringToTop the world — that buried the HUD scenes underneath it, so the whole UI (menu,
      // currency, minimap) vanished after a duel. Instead, restore the HUD scenes ABOVE the world.
      [SCENE_KEYS.CURRENCY_HUD_SCENE, SCENE_KEYS.WORLD_HUD_SCENE].forEach((hudKey) => {
        if (this.scene.isActive(hudKey)) this.scene.bringToTop(hudKey);
        else this.scene.launch(hudKey);
      });
    } else {
      this.scene.start(SCENE_KEYS.WORLD_SCENE);
    }
  }
}
